import { Capacitor } from '@capacitor/core'
import { App } from '@capacitor/app'
import { Browser } from '@capacitor/browser'
import { Keyboard } from '@capacitor/keyboard'
import { SplashScreen } from '@capacitor/splash-screen'
import { StatusBar, Style } from '@capacitor/status-bar'
import { externalUrl, hexColor } from './nativeLinks.js'

/*
	Glue between the web app and the Capacitor shell (see NATIVE-WRAPPER.md). Does nothing in
	a browser. Call installNative() before installTheme() so the first ihh:appearance event
	reaches the status bar.
*/

export const isNative = () => Capacitor.isNativePlatform()

const ignore = () => {}

function installStatusBar() {
	const android = Capacitor.getPlatform() === 'android'
	window.addEventListener('ihh:appearance', (event) => {
		const { dark, statusBarColor } = event.detail || {}
		// Style.Dark = light text, for the dark theme; Style.Light = dark text.
		StatusBar.setStyle({ style: dark ? Style.Dark : Style.Light }).catch(ignore)
		// iOS draws the page under the status bar; Android gives the bar its own colour.
		const color = android && hexColor(statusBarColor)
		if (color) StatusBar.setBackgroundColor({ color }).catch(ignore)
	})
}

// Android back: close the open sheet / go back a page (both are history entries); on the
// first page, send the app to the background rather than closing it.
function installBackButton() {
	if (Capacitor.getPlatform() !== 'android') return
	App.addListener('backButton', ({ canGoBack }) => {
		if (canGoBack) window.history.back()
		else App.minimizeApp().catch(ignore)
	})
}

// Off-site links and window.open calls open in an in-app browser tab instead of replacing
// the app's web view. Runs after the page's own handlers (website.js routes inhishands.com
// links inside the app and marks the click handled).
function installExternalLinks() {
	window.addEventListener('click', (event) => {
		if (event.defaultPrevented || event.button !== 0) return
		const link = event.target?.closest?.('a[href]')
		const url = link && externalUrl(link.getAttribute('href'), window.location.href)
		if (!url) return
		event.preventDefault()
		Browser.open({ url }).catch(ignore)
	})

	const open = window.open.bind(window)
	window.open = (href, ...rest) => {
		const url = externalUrl(typeof href === 'string' ? href : href?.toString?.(), window.location.href)
		if (!url) return open(href, ...rest)
		Browser.open({ url }).catch(ignore)
		return null
	}
}

function installKeyboard() {
	if (Capacitor.getPlatform() === 'ios') Keyboard.setAccessoryBarVisible({ isVisible: false }).catch(ignore)
}

export function installNative() {
	if (!isNative()) return
	installStatusBar()
	installBackButton()
	installExternalLinks()
	installKeyboard()
}

// The launch screen stays up until the first page has rendered (launchAutoHide: false).
export function hideSplash() {
	if (!isNative()) return
	SplashScreen.hide({ fadeOutDuration: 200 }).catch(ignore)
}
